// Amount utility functions for trade entry
//
// The platform shorthand standard (operator, reconfirmed 2026-09-16):
//   M / m   → thousands       500M  → 500,000
//   MM      → millions        10MM  → 10,000,000
//   MMM / B → billions        1.5B  → 1,500,000,000
//   K / k   → thousands (accepted alias of M)
// Commas, spaces and a leading currency symbol are ignored: "$10,000,000" → 10,000,000.
// Fields replace the shorthand with the full number in place on blur (see replaceShorthandOnBlur).

export const parseAmountShorthand = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined) return 0;

  const trimmed = String(value).trim().toUpperCase().replace(/[,\s]/g, '').replace(/^[$€£¥]/, '');
  if (trimmed === '') return 0;
  const parseNumeric = (raw: string): number => {
    if (!/^(?:\d+|\d*\.\d+)$/.test(raw)) return 0;
    const num = Number(raw);
    return Number.isFinite(num) ? num : 0;
  };

  const match = trimmed.match(/^(.*?)(MMM|MM|M|K|B)$/);
  if (!match) return parseNumeric(trimmed);
  const num = parseNumeric(match[1]);
  switch (match[2]) {
    case 'K':
    case 'M': return num * 1_000;
    case 'MM': return num * 1_000_000;
    case 'B':
    case 'MMM': return num * 1_000_000_000;
    default: return 0;
  }
};

/** True when the text parses to a positive finite amount. */
export const isValidAmountShorthand = (value: string | number | null | undefined): boolean => {
  const parsed = parseAmountShorthand(value);
  return Number.isFinite(parsed) && parsed > 0;
};

/**
 * The in-field expansion every amount input does on blur: "10MM" becomes "10,000,000" in
 * the same box. Returns the text unchanged when it does not parse, so a typo stays visible.
 */
export const replaceShorthandOnBlur = (value: string): string => {
  const parsed = parseAmountShorthand(value);
  return parsed > 0 ? formatAmountWithCommas(parsed) : value;
};

export const formatAmountWithCommas = (value: number): string => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '';

  return numericValue.toLocaleString('en-US', {
    maximumFractionDigits: 6,
  });
};

/** Money for messages and labels: "$10,000,000" (no cents unless present). */
export const formatMoney = (value: number | string | null | undefined, currency = 'USD'): string => {
  if (value === null || value === undefined || value === '') return '';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '';
  const hasCents = Math.abs(numericValue % 1) > 0.000001;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  }).format(numericValue);
};

export const formatAmountShorthand = (value: number): string => {
  if (!value || value === 0) return '';

  if (value >= 1000000000 && value % 1000000000 === 0) {
    return `${value / 1000000000}MMM`;
  } else if (value >= 1000000 && value % 1000000 === 0) {
    return `${value / 1000000}MM`;
  } else if (value >= 1000 && value % 1000 === 0) {
    return `${value / 1000}M`;
  } else {
    return value.toString();
  }
};


